import { z } from "zod";

/**
 * Tenancy model (see docs/decisions/0004-multi-tenancy.md):
 * an instance hosts many tenants (root organisations); each tenant owns a tree
 * of entities (subsidiaries, business units). Config resolves global → tenant → entity.
 */
export const tenantIdSchema = z.string().brand<"TenantId">();
export const entityIdSchema = z.string().brand<"EntityId">();
export const userIdSchema = z.string().brand<"UserId">();

export type TenantId = z.infer<typeof tenantIdSchema>;
export type EntityId = z.infer<typeof entityIdSchema>;
export type UserId = z.infer<typeof userIdSchema>;

/** Route parameters and bodies carry IDs as UUIDs; branding happens after validation. */
export const uuidSchema = z.uuid();

/**
 * Roles are granted per entity membership.
 * - `tenant_admin`: on the root entity only; manages the whole tenant (IdP, domains, settings).
 * - `entity_admin`: manages the entity it is granted on and its whole subtree.
 * - `member`: uses ESI in that entity.
 */
export const roleSchema = z.enum(["tenant_admin", "entity_admin", "member"]);
export type Role = z.infer<typeof roleSchema>;

/**
 * `deletion_scheduled` (30-day grace period) and `deleted` (purged) are refused like
 * `suspended`: no session or token of the tenant's users works.
 */
export const tenantStatusSchema = z.enum(["active", "suspended", "deletion_scheduled", "deleted"]);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

export const tenantSchema = z.object({
  id: tenantIdSchema,
  name: z.string(),
  status: tenantStatusSchema,
  origin: z.enum(["self_signup", "managed"]),
  /** The operators' OCR override (spec `tenant-plans`); null follows the plan. */
  ocrMonthlyPages: z.int().min(0).nullable(),
  planId: z.string(),
  /** End of a scheduled deletion's grace period. */
  deletionDueAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type Tenant = z.infer<typeof tenantSchema>;

export const entitySchema = z.object({
  id: entityIdSchema,
  parentId: entityIdSchema.nullable(),
  name: z.string(),
  /** Ancestors from the root down to and including this entity. */
  path: z.array(entityIdSchema).min(1),
});
export type Entity = z.infer<typeof entitySchema>;

export const userSchema = z.object({
  id: userIdSchema,
  email: z.email(),
  displayName: z.string().nullable(),
  status: z.enum(["active", "disabled"]),
  defaultEntityId: entityIdSchema,
});
export type User = z.infer<typeof userSchema>;

export const membershipSchema = z.object({
  entityId: entityIdSchema,
  userId: userIdSchema,
  roles: z.array(roleSchema).min(1),
});
export type Membership = z.infer<typeof membershipSchema>;

const nameSchema = z.string().trim().min(1).max(200);

// Request bodies. Zod objects strip unknown keys, so a forged `tenantId` never reaches a
// service; the tenant always comes from the authenticated identity.
/** Self-signup: never a plan of its own choosing (the instance's default plan applies). */
export const selfSignupRequestSchema = z.object({
  name: nameSchema,
  adminEmail: z.email(),
  adminName: nameSchema.optional(),
});
/** Operators: optionally another plan than the instance's default. */
export const createTenantRequestSchema = selfSignupRequestSchema.extend({
  planId: uuidSchema.optional(),
});
export const renameTenantRequestSchema = z.object({ name: nameSchema });
export const createEntityRequestSchema = z.object({ parentId: uuidSchema, name: nameSchema });
export const addMemberRequestSchema = z.object({
  email: z.email(),
  displayName: nameSchema.optional(),
  roles: z.array(roleSchema).min(1),
});
export const setRolesRequestSchema = z.object({ roles: z.array(roleSchema).min(1) });

export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;
export type CreateEntityRequest = z.infer<typeof createEntityRequestSchema>;
export type AddMemberRequest = z.infer<typeof addMemberRequestSchema>;
